/**
 * ZELADOR DE DECODER — todo `<video>` que sai do DOM precisa devolver o decoder.
 *
 * <p><b>O vazamento.</b> `releaseAndroidVideoDecoder` só roda dentro do `applyVideoSource`,
 * isto é, quando a FONTE muda num elemento que continua MONTADO. Quando o React desmonta o
 * elemento, ninguém solta nada: o nó sai do DOM com o `src` setado e `readyState=4`, e como
 * nenhum caminho de código volta a tocá-lo, o decoder de HW fica preso até o GC — que no A7
 * pode não vir nunca.</p>
 *
 * <p><b>Medido no Tab A7 (SM-T505), 1.0.465, 2026-10-10.</b> Um gatilho de IMAGEM troca a
 * playlist inteira e desmonta o bloco de vídeo. No instante do disparo:</p>
 * <pre>
 *   &lt;video&gt; criados        3 → 6     (o React recriou o bloco)
 *   órfãos (fora do DOM)    0 → 3
 *   órfãos AINDA com src    0 → 2     ← os dois seguram decoder, rs=4
 *   DOM                     A e B na MESMA fonte, ambos MEDIA_ERR_DECODE
 * </pre>
 * <p>O A7 não tem decoder sobrando, então os elementos novos não conseguem alocar
 * (`internalError -12`, 12/min) e o player fica preto sem se recuperar — até o processo morrer.
 * Soltar os dois órfãos à mão devolveu o playback na hora, sem restart: é prova de que soltar
 * FUNCIONA mesmo com o elemento já destacado, e de que o problema é a soltura nunca acontecer.</p>
 *
 * <p><b>Por que um zelador, e não um cleanup no ponto certo.</b> O `<video>` do Stream desmonta
 * por vários caminhos (gatilho, zona, stream, held-frame, troca de motor). Amarrar a soltura a
 * um `useEffect` de um deles resolve um caminho e deixa os outros — foi exatamente o que
 * aconteceu na 1.0.465, cujo cleanup não roda neste caminho (medido: os órfãos saíram com o
 * `src` intacto). O zelador garante a invariante por OBSERVAÇÃO do DOM, então vale pra todo
 * caminho de desmontagem, inclusive os que ainda não existem.</p>
 *
 * <p><b>Por que o atraso.</b> O React às vezes remove e reinsere o MESMO nó (o hold-frame
 * depende disso: "ficando sempre no mesmo índice de filho, o React preserva o nó"). Soltar na
 * hora da remoção arrancaria o vídeo de um nó que vai voltar. O zelador então só solta o que
 * CONTINUA fora do DOM depois do atraso — remoção seguida de reinserção é ignorada.</p>
 */

/** Espera antes de varrer: separa desmontagem de verdade de um remove+reinsere do React. */
export const ATRASO_DE_VARREDURA_MS = 500;

export interface OpcoesDoZelador {
  /** Subárvore observada. Na prática, `document.body`. */
  raiz: Node;
  /** Solta o decoder de um elemento já destacado. */
  soltar: (video: HTMLVideoElement) => void;
  /** Sobrescreve o atraso (testes). */
  atrasoMs?: number;
  /** Agendador (testes injetam um relógio falso). */
  agendar?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  /** Cancelamento do agendador (testes). */
  cancelar?: (id: ReturnType<typeof setTimeout>) => void;
}

/** Os `<video>` que saíram junto com este nó (ele próprio ou descendentes). */
function videosRemovidos(no: Node): HTMLVideoElement[] {
  if (no.nodeType !== 1) return [];
  const el = no as Element;
  if (el.tagName === 'VIDEO') return [el as HTMLVideoElement];
  return Array.from(el.querySelectorAll?.('video') ?? []);
}

/** Um elemento destacado só segura decoder enquanto tiver fonte. */
function aindaSeguraDecoder(video: HTMLVideoElement): boolean {
  return !video.isConnected && !!(video.currentSrc || video.src);
}

/**
 * Observa `raiz` e solta o decoder de todo `<video>` que ficar fora do DOM.
 *
 * @returns função que desliga o zelador (cancela a varredura pendente).
 */
export function criarZeladorDeDecoder(opcoes: OpcoesDoZelador): () => void {
  const {
    raiz,
    soltar,
    atrasoMs = ATRASO_DE_VARREDURA_MS,
    agendar = setTimeout,
    cancelar = clearTimeout,
  } = opcoes;

  const pendentes = new Set<HTMLVideoElement>();
  let varredura: ReturnType<typeof setTimeout> | null = null;

  const varrer = (): void => {
    varredura = null;
    for (const video of pendentes) {
      // `aindaSeguraDecoder` exige estar FORA do DOM: um nó que o React removeu e
      // reinseriu (hold-frame) já sai daqui sozinho, sem precisar de guarda própria.
      if (aindaSeguraDecoder(video)) {
        try {
          soltar(video);
        } catch {
          /* best-effort: o zelador nunca pode derrubar o playback */
        }
      }
    }
    pendentes.clear();
  };

  const observador = new MutationObserver((mutacoes) => {
    for (const m of mutacoes) {
      for (const no of Array.from(m.removedNodes)) {
        for (const video of videosRemovidos(no)) pendentes.add(video);
      }
    }
    if (pendentes.size > 0 && varredura === null) {
      varredura = agendar(varrer, atrasoMs);
    }
  });

  observador.observe(raiz, { childList: true, subtree: true });

  return () => {
    observador.disconnect();
    if (varredura !== null) cancelar(varredura);
    varredura = null;
    pendentes.clear();
  };
}

/** A soltura em si — o mesmo teardown do `releaseAndroidVideoDecoder`, sem o yield. */
export function soltarDecoderDoVideo(video: HTMLVideoElement): void {
  video.pause();
  video.removeAttribute('src');
  video.load();
}
